import React, { useEffect, useState } from "react";
import { StageChip } from "../dashboard/primitives";
import { api, fromCents } from "../lib/api";
import StartSubscriptionModal from "./StartSubscriptionModal";
import { createSubscriptionCheckoutSession } from "../lib/billing";
import { Skeleton } from "./Skeleton";
import { useMinLoading } from "../lib/useMinLoading";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const fmtTs = (unix) => {
  if (!unix) return "—";
  const d = typeof unix === "number" ? new Date(unix * 1000) : new Date(unix);
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

const chipFor = (status) => {
  if (status === "active" || status === "trialing") return "paid";
  if (status === "past_due" || status === "unpaid") return "awaiting_approval";
  if (status === "canceled" || status === "incomplete_expired") return "closed";
  return "in_progress";
};

const SubscriptionPanel = ({ project, owner }) => {
  const [sub, setSub] = useState(null);
  const [priceId, setPriceId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hostedUrl, setHostedUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const ownerUid = project.ownerUid;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/subscription/${ownerUid}`).catch(() => ({ data: null }));
      setSub(res?.data?.subscription || res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ownerUid]);

  const doAction = async (path) => {
    const id = sub?.id || sub?.subscriptionId;
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(path, { subscriptionId: id });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const showSkeleton = useMinLoading(loading);

  if (showSkeleton)
    return (
      <div style={{ display: "grid", gap: 8 }} aria-busy="true" aria-label="Loading subscription">
        <Skeleton width={90} height={20} />
        <Skeleton width="40%" height={14} />
        <Skeleton width="60%" height={12} />
        <Skeleton width="30%" height={12} />
      </div>
    );

  if (!sub) {
    return (
      <>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 13, color: "var(--fw-muted)" }}>
            No active subscription for this client. Paste the Stripe Price ID for the
            {" "}{project.paymentPlan === "lease" ? "12-month lease" : "24-month rent-to-own"} plan,
            then start the checkout flow — the client enters their card in the modal and the
            subscription activates on first charge.
          </div>
          <input
            className="fw-input"
            placeholder="price_..."
            value={priceId}
            onChange={(e) => setPriceId(e.target.value.trim())}
          />
          {error && <div style={{ color: "#c0392b", fontSize: 12 }}>{error}</div>}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="fw-btn fw-btn--primary"
              disabled={!priceId}
              onClick={() => setShowModal(true)}
            >
              Collect card now
            </button>
            <button
              className="fw-btn fw-btn--ghost"
              disabled={!priceId || busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                setHostedUrl(null);
                try {
                  const { url } = await createSubscriptionCheckoutSession({
                    user: { uid: ownerUid, email: owner?.email },
                    profile: owner,
                    priceId,
                    projectId: project.id,
                  });
                  setHostedUrl(url);
                } catch (err) {
                  setError(err?.response?.data?.error || err.message || "Could not create Checkout session");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Creating…" : "Generate hosted link"}
            </button>
          </div>
          {hostedUrl && (
            <div
              style={{
                padding: 10,
                border: "1px solid var(--fw-line)",
                borderRadius: "var(--fw-r-sm)",
                background: "var(--fw-cream-2)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontFamily: "var(--fw-mono)", fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--fw-muted)" }}>
                Send this Stripe-hosted Checkout link to the client
              </div>
              <code style={{ fontSize: 12, wordBreak: "break-all" }}>{hostedUrl}</code>
              <div style={{ display: "flex", gap: 6 }}>
                <a className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px" }} href={hostedUrl} target="_blank" rel="noreferrer">
                  Open
                </a>
                <button
                  className="fw-btn fw-btn--ghost"
                  style={{ padding: "6px 10px" }}
                  onClick={() => {
                    navigator.clipboard.writeText(hostedUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>
        {showModal && (
          <StartSubscriptionModal
            project={project}
            owner={owner}
            priceId={priceId}
            onClose={() => setShowModal(false)}
            onStarted={load}
          />
        )}
      </>
    );
  }

  const cents =
    sub.items?.data?.[0]?.price?.unit_amount ??
    sub.plan?.amount ??
    sub.latest_invoice?.amount_due ??
    null;
  const interval = sub.items?.data?.[0]?.price?.recurring?.interval || sub.plan?.interval;
  const cancelAtPeriodEnd = sub.cancel_at_period_end || sub.cancelAtPeriodEnd;
  const periodEnd = sub.current_period_end || sub.currentPeriodEnd || sub.items?.data?.[0]?.current_period_end;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        <StageChip status={chipFor(sub.status)} />
        {cents !== null && (
          <span style={{ fontFamily: "var(--fw-mono)", fontSize: 13 }}>
            {fmt(fromCents(cents))}{interval ? ` / ${interval}` : ""}
          </span>
        )}
        <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
          Current period ends {fmtTs(periodEnd)}
        </span>
        {cancelAtPeriodEnd && (
          <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "#8a5a00" }}>
            Cancels at period end
          </span>
        )}
      </div>
      {error && <div style={{ color: "#c0392b", fontSize: 12 }}>{error}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {!cancelAtPeriodEnd && sub.status !== "canceled" && (
          <button className="fw-btn fw-btn--ghost" onClick={() => doAction("/cancel-subscription")} disabled={busy}>
            Cancel at period end
          </button>
        )}
        {cancelAtPeriodEnd && (
          <button className="fw-btn fw-btn--ghost" onClick={() => doAction("/reactivate-subscription")} disabled={busy}>
            Reactivate
          </button>
        )}
        {sub.status !== "canceled" && (
          <button
            className="fw-btn fw-btn--ghost"
            style={{ color: "#c0392b" }}
            onClick={() => {
              if (window.confirm("Cancel this subscription immediately? This stops billing now.")) {
                doAction("/cancel-subscription-immediately");
              }
            }}
            disabled={busy}
          >
            Cancel now
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPanel;
